import { Bell, BellOff, Loader2, Send, Smartphone } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { pushBlockedOnIOS, pushSupported, usePush } from "./usePush";

const LABELS = {
  nl: {
    inbound_message: ["Nieuw inkomend bericht", "WhatsApp, sms of e-mail in je inbox"],
    clause_alert: ["Clausule nadert of is getriggerd", "Clause Radar-updates over je spelers"],
    portal_update: ["Nieuw gedeeld document of clausule-update", "Zodra je agent iets met je deelt"],
    on: "Meldingen aanzetten", off: "Meldingen uitzetten", test: "Testmelding sturen", active: "Meldingen staan aan op dit apparaat",
    inactive: "Meldingen staan uit", unsupported: "Deze browser ondersteunt geen meldingen.",
    ios: "Op iPhone werken meldingen alleen als Playmaker op het beginscherm staat (iOS 16.4+): tik op Deel → Zet op beginscherm en open de app vanaf daar.",
    privacy: "Meldingen bevatten nooit bedragen of contractinhoud — details zie je pas na openen.", devices: "apparaten",
  },
  en: {
    inbound_message: ["New inbound message", "WhatsApp, SMS or e-mail in your inbox"],
    clause_alert: ["Clause approaching or triggered", "Clause Radar updates about your players"],
    portal_update: ["New shared document or clause update", "Whenever your agent shares something with you"],
    on: "Enable notifications", off: "Disable notifications", test: "Send test notification", active: "Notifications are on for this device",
    inactive: "Notifications are off", unsupported: "This browser does not support notifications.",
    ios: "On iPhone, notifications only work when Playmaker is on your home screen (iOS 16.4+): tap Share → Add to Home Screen and open the app from there.",
    privacy: "Notifications never contain amounts or contract content — details only after opening.", devices: "devices",
  },
};

export function PushSettings({ lang = "nl", dark = false }) {
  const L = LABELS[lang] || LABELS.nl;
  const { status, subscribed, busy, error, enable, disable, setPref, sendTest } = usePush();
  const supported = pushSupported();
  const cats = status?.categories || [];
  const prefs = status?.prefs || {};
  const btn = dark ? "bg-emerald-400 text-[#0A0F0C]" : "bg-[#0A0F0C] text-white";

  return (
    <div className="space-y-4" data-testid="push-settings">
      <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${subscribed ? "bg-emerald-50 text-emerald-900" : "bg-slate-100 text-slate-700"}`} data-testid="push-status">
        {subscribed ? <Bell size={18} className="text-emerald-600" /> : <BellOff size={18} className="text-slate-400" />}
        <div className="text-sm font-medium flex-1">{subscribed ? L.active : L.inactive}{status?.devices ? <span className="text-xs font-normal opacity-70"> · {status.devices} {L.devices}</span> : null}</div>
      </div>
      {pushBlockedOnIOS() && (
        <div className="flex gap-2 text-xs rounded-xl bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 leading-relaxed" data-testid="push-ios-hint"><Smartphone size={14} className="shrink-0 mt-0.5" />{L.ios}</div>
      )}
      {!supported && <div className="text-xs text-slate-500" data-testid="push-unsupported">{L.unsupported}</div>}
      <div className="flex flex-wrap gap-2">
        {!subscribed ? (
          <button type="button" onClick={enable} disabled={busy || !supported} className={`h-11 px-5 rounded-full text-sm font-bold flex items-center gap-2 disabled:opacity-50 ${btn}`} data-testid="push-enable-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Bell size={15} />} {L.on}
          </button>
        ) : (
          <>
            <button type="button" onClick={sendTest} disabled={busy} className={`h-11 px-5 rounded-full text-sm font-bold flex items-center gap-2 disabled:opacity-50 ${btn}`} data-testid="push-test-btn">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {L.test}
            </button>
            <button type="button" onClick={disable} disabled={busy} className="h-11 px-5 rounded-full text-sm font-semibold border border-slate-200 bg-white text-slate-700 disabled:opacity-50" data-testid="push-disable-btn">{L.off}</button>
          </>
        )}
      </div>
      {error && <div className="text-xs text-rose-600" data-testid="push-error">{error}</div>}
      {cats.length > 0 && (
        <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="push-prefs">
          {cats.map((c) => (
            <label key={c} className="flex items-center gap-3 px-4 py-3 min-h-[56px] cursor-pointer">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">{L[c]?.[0] || c}</div>
                <div className="text-xs text-slate-500">{L[c]?.[1]}</div>
              </div>
              <Switch checked={prefs[c] !== false} onCheckedChange={(v) => setPref(c, v)} data-testid={`push-pref-${c}`} />
            </label>
          ))}
        </div>
      )}
      <div className="text-[11px] text-slate-500" data-testid="push-privacy-note">{L.privacy}</div>
    </div>
  );
}
